import { ClickAwayListener } from '@mui/material'
import clsx from 'clsx'
import CommonButton from 'components/Button/ButtonTypes/CommonButton'
import { ReactComponent as AddSvg } from 'images/icons/add_symbol.svg'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { ReactComponent as DrawZoneSvg } from 'images/icons/draw_zone.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/routing.svg'
import { ReactComponent as RouteFinishSvg } from 'images/icons/transfer_route_finish.svg'
import { ReactComponent as RouteStartSvg } from 'images/icons/transfer_route_start.svg'
import cloneDeep from 'lodash.clonedeep'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import { selectMapMode, selectMissionFinishPoint, selectMissionStartPoint } from 'store/selectors/mission'
import { MISSION_MAP_DRAWING_MODES, MISSION_PAGE_MODES, MISSION_ZONE_TYPES } from 'utils/constants'
import { IZone } from 'utils/interfaces'

import style from '../MissionButtons.module.css'

enum DROPDOWN_STATES {
  ADD_OBJECT,
  DRAW_CLEANING_ZONE,
  DRAW_RESTRICTED_ZONE,
  ADD_ROUTE,
  DRAW_START_POINT,
  DRAW_FINISH_POINT,
}

const AddObjectContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className="text-16-500">{t('mission.add_object')}</span>
      </div>
    </div>
  )
}

const DrawCleaningZoneContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className={style.complexButtonTextTitle}>Cleaning Zone</span>
      </div>
    </div>
  )
}

const DrawRestrictedZoneContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className={style.complexButtonTextTitle}>Restricted Zone</span>
      </div>
    </div>
  )
}

const AddRouteContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className={style.complexButtonTextTitle}>{t('mission.add_route')}</span>
      </div>
    </div>
  )
}

const AddStartPointContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className={style.complexButtonTextTitle}>{t('mission.add_start_point')}</span>
      </div>
    </div>
  )
}

const AddFinishPointContent = () => {
  const { t } = useTranslation()

  return (
    <div className={style.complexButtonContent}>
      <div className={style.complexButtonDesc}>
        <span className={style.complexButtonTextTitle}>{t('mission.add_finish_point')}</span>
      </div>
    </div>
  )
}

export const DrawDropdown = ({ className }: { className?: string }) => {
  const mapMode = useSelector(selectMapMode)
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [dropdownState, setDropdownState] = useState(DROPDOWN_STATES.ADD_OBJECT)
  const startPoint = useSelector(selectMissionStartPoint)
  const finishPoint = useSelector(selectMissionFinishPoint)

  const dropdownActive = useMemo(() => {
    return mapMode !== MISSION_MAP_DRAWING_MODES.VIEW_ZONES
  }, [mapMode])

  const clearTempData = () => {
    dispatch(MissionActions.setTempLaunchPoint(undefined))
    dispatch(MissionActions.setTempZoneRoute(undefined))
    dispatch(MissionActions.setTempZone(undefined))
  }

  const onAddObjectButtonClicked = useCallback(() => {
    setDropdownState(DROPDOWN_STATES.ADD_OBJECT)
  }, [dispatch])

  const onDrawCleaningZoneClicked = useCallback(() => {
    clearTempData()
    setDropdownState(DROPDOWN_STATES.DRAW_CLEANING_ZONE)
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.CLEANING))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ZONE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_POLYGON))
  }, [dispatch])

  const onDrawRestrictedZoneClicked = useCallback(() => {
    clearTempData()
    setDropdownState(DROPDOWN_STATES.DRAW_RESTRICTED_ZONE)
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.RESTRICT))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ZONE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_POLYGON))
  }, [dispatch])

  const onAddRouteButtonClicked = useCallback(() => {
    clearTempData()
    setDropdownState(DROPDOWN_STATES.ADD_ROUTE)
    dispatch(MissionActions.setDrawingZoneType(MISSION_ZONE_TYPES.RECORDED))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ROUTE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_ROUTE)) // should be fixed
  }, [dispatch])

  const onAddStartPointButtonClicked = useCallback(() => {
    clearTempData()
    setDropdownState(DROPDOWN_STATES.DRAW_START_POINT)
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT))
  }, [dispatch])

  const onAddFinishPointButtonClicked = useCallback(() => {
    clearTempData()
    setDropdownState(DROPDOWN_STATES.DRAW_FINISH_POINT)
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT))
  }, [dispatch])

  const onClick = (): void => {
    setOpen(prev => !prev)
  }

  const onClickAway = (): void => {
    setOpen(prev => !prev)
  }

  const contentsList = {
    [`${DROPDOWN_STATES.ADD_OBJECT}`]: <AddObjectContent />,
    [`${DROPDOWN_STATES.DRAW_CLEANING_ZONE}`]: <DrawCleaningZoneContent />,
    [`${DROPDOWN_STATES.DRAW_RESTRICTED_ZONE}`]: <DrawRestrictedZoneContent />,
    [`${DROPDOWN_STATES.ADD_ROUTE}`]: <AddRouteContent />,
    [`${DROPDOWN_STATES.DRAW_START_POINT}`]: <AddStartPointContent />,
    [`${DROPDOWN_STATES.DRAW_FINISH_POINT}`]: <AddFinishPointContent />,
  }

  return (
    <div
      className={clsx(
        style.button,
        style.buttonWide,
        style.positionRelative,
        dropdownActive ? style.active : '',
        className
      )}
      onClick={onClick}
    >
      {contentsList[dropdownState]}
      <ArrowSvg className={style.dropdownArrow} />
      {open ? (
        <ClickAwayListener onClickAway={onClickAway}>
          <div className={style.dropdown}>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onAddObjectButtonClicked}
            >
              <AddObjectContent />
            </CommonButton>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onDrawCleaningZoneClicked}
            >
              <DrawCleaningZoneContent />
            </CommonButton>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onAddRouteButtonClicked}
            >
              <AddRouteContent />
            </CommonButton>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onAddStartPointButtonClicked}
              disabled={!!startPoint}
            >
              <AddStartPointContent />
            </CommonButton>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onAddFinishPointButtonClicked}
              disabled={!!finishPoint}
            >
              <AddFinishPointContent />
            </CommonButton>
            <CommonButton
              className={clsx(style.bottomShadow, style.button, style.buttonWide)}
              onClick={onDrawRestrictedZoneClicked}
            >
              <DrawRestrictedZoneContent />
            </CommonButton>
          </div>
        </ClickAwayListener>
      ) : null}
    </div>
  )
}
